import React, { useState } from 'react';
import { Card, Descriptions, Typography, Space, Tag, Image, Button, Table, Modal, Alert } from 'antd';
import { UserOutlined, PhoneOutlined, CalendarOutlined, EnvironmentOutlined, TrophyOutlined, FileOutlined, EyeOutlined, DownloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import config, { SUBJECTS } from '../../config';
import dayjs from 'dayjs';

const { Title } = Typography;

const formatDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '';

const StudentProfileReadOnly = ({ data, attachmentTypes }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    const isImageFile = (mimeType) => mimeType && mimeType.startsWith('image/');
    const isPdfFile = (mimeType) => mimeType === 'application/pdf';

    const handlePreview = (attachment) => {
        setPreviewData(attachment);
        setPreviewOpen(true);
    };

    const getAttachmentTypeName = (typeId) => {
        const type = (attachmentTypes || []).find((t) => t.id === typeId);
        return type ? type.name : 'Tài liệu';
    };

    const renderGrades = (grades) => {
        if (!grades) return null;

        const subjects = Object.entries(SUBJECTS).map(([key, label]) => ({ key, label }));

        const columns = [
            { title: 'Môn học', dataIndex: 'label', key: 'label', width: 200, fixed: 'left' },
            { title: 'Lớp 10', key: 'grade_10', align: 'center', render: (_, record) => grades[`${record.key}_10`] || 'N/A' },
            { title: 'Lớp 11', key: 'grade_11', align: 'center', render: (_, record) => grades[`${record.key}_11`] || 'N/A' },
            { title: 'Lớp 12', key: 'grade_12', align: 'center', render: (_, record) => grades[`${record.key}_12`] || 'N/A' },
        ];

        return (
            <Card title="Kết quả học tập" style={{ marginBottom: 16 }}>
                <Table
                    columns={columns}
                    dataSource={subjects}
                    rowKey="key"
                    pagination={false}
                    size="middle"
                    bordered
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        );
    };

    const renderAdmissions = (admissions, firstAdmission) => {
        const allAdmissions = [
            ...(firstAdmission ? [{
                id: 'first_admission',
                priority: 0,
                code: firstAdmission.code,
                name: firstAdmission.name,
                isFirstAdmission: true,
            }] : []),
            ...(admissions || []),
        ];

        if (allAdmissions.length === 0) return null;

        const columns = [
            {
                title: 'Nguyện vọng',
                dataIndex: 'priority',
                key: 'priority',
                width: 140,
                render: (priority, record) => record.isFirstAdmission
                    ? <Tag color="green">Nguyện vọng 1</Tag>
                    : `Nguyện vọng ${priority}`,
            },
            { title: 'Mã ngành', dataIndex: 'code', key: 'code', width: 120 },
            { title: 'Tên ngành', dataIndex: 'name', key: 'name', ellipsis: true },
        ];

        return (
            <Card title="Nguyện vọng xét tuyển" style={{ marginBottom: 16 }}>
                <Table columns={columns} dataSource={allAdmissions} rowKey="id" pagination={false} size="middle" bordered />
            </Card>
        );
    };

    const renderAttachments = (attachments) => {
        if (!attachments || attachments.length === 0) return null;

        const handleDownload = (attachment) => {
            const link = document.createElement('a');
            link.href = attachment.file_url;
            link.download = attachment.file_name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return (
            <>
                <Card title="Tài liệu đính kèm" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {attachments.map((attachment) => (
                            <Card key={attachment.id} size="small" title={getAttachmentTypeName(attachment.attachment_type_id)}>
                                <Space wrap>
                                    <FileOutlined />
                                    <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
                                        {attachment.file_name}
                                    </a>
                                    <Tag color="blue">{(attachment.file_size).toFixed ? `${Number(attachment.file_size).toFixed(2)} MB` : ''}</Tag>
                                    <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownload(attachment)}>
                                        Tải xuống
                                    </Button>
                                    {isImageFile(attachment.mime_type) && (
                                        <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(attachment)}>
                                            Xem trước
                                        </Button>
                                    )}
                                </Space>
                            </Card>
                        ))}
                    </Space>
                </Card>

                <Modal
                    title={previewData?.file_name}
                    open={previewOpen}
                    onCancel={() => setPreviewOpen(false)}
                    footer={null}
                    width={800}
                    style={{ top: 20 }}
                >
                    {previewData && (
                        <div style={{ textAlign: 'center' }}>
                            {isImageFile(previewData.mime_type) ? (
                                <Image src={previewData.file_url} alt={previewData.file_name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                            ) : isPdfFile(previewData.mime_type) ? (
                                <iframe src={previewData.file_url} style={{ width: '100%', height: '70vh', border: 'none' }} title={previewData.file_name} />
                            ) : null}
                        </div>
                    )}
                </Modal>
            </>
        );
    };

    const renderPayment = (payment) => {
        if (!payment) return null;

        const attachmentUrl = payment.attachment
            ? `${config.apiUrl}/storage/${payment.attachment.replace('public/', '')}`
            : null;

        const handlePreviewPayment = () => {
            setPreviewData({
                file_url: attachmentUrl,
                file_name: `Minh chứng nộp lệ phí - ${payment.created_at ? dayjs(payment.created_at).format('DD/MM/YYYY HH:mm') : ''}`,
                mime_type: 'image/jpeg',
            });
            setPreviewOpen(true);
        };

        const handleDownloadPayment = () => {
            const link = document.createElement('a');
            link.href = attachmentUrl;
            link.download = `minh-chung-le-phi.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return (
            <Card title="Thông tin thanh toán lệ phí" style={{ marginBottom: 16 }}>
                <Descriptions bordered>
                    <Descriptions.Item label="Trạng thái" span={3}>
                        <Tag color={payment.is_paid ? 'green' : 'orange'}>
                            {payment.is_paid ? 'Đã nộp lệ phí' : 'Chưa xác nhận'}
                        </Tag>
                    </Descriptions.Item>
                    {attachmentUrl && (
                        <>
                            <Descriptions.Item label="Minh chứng" span={3}>
                                <Space>
                                    <FileOutlined />
                                    <span>Hình ảnh minh chứng đã tải lên</span>
                                    <Button type="link" icon={<EyeOutlined />} onClick={handlePreviewPayment}>
                                        Xem trước
                                    </Button>
                                    <Button type="link" icon={<DownloadOutlined />} onClick={handleDownloadPayment}>
                                        Tải xuống
                                    </Button>
                                </Space>
                            </Descriptions.Item>
                            {payment.created_at && (
                                <Descriptions.Item label="Ngày tải lên" span={3}>
                                    <Space>
                                        <CalendarOutlined />
                                        {dayjs(payment.created_at).format('DD/MM/YYYY HH:mm')}
                                    </Space>
                                </Descriptions.Item>
                            )}
                        </>
                    )}
                </Descriptions>
            </Card>
        );
    };

    if (!data) return null;

    const period = (data.period && typeof data.period === 'object') ? data.period : null;
    const isUpcoming = period?.status === 'upcoming';

    return (
        <>
            <Alert
                type="warning"
                showIcon
                icon={<ClockCircleOutlined />}
                style={{ marginBottom: 24 }}
                message={<strong>{isUpcoming ? 'Đợt tuyển sinh sắp mở' : 'Đợt tuyển sinh đã đóng'}</strong>}
                description={
                    isUpcoming
                        ? `Đợt tuyển sinh "${period?.name || ''}" sẽ bắt đầu từ ngày ${formatDate(period?.start_date)}. Bạn chỉ có thể xem lại thông tin hiện có, chưa thể chỉnh sửa.`
                        : `Đợt tuyển sinh gắn với hồ sơ của bạn đã đóng, bạn chỉ có thể xem lại thông tin đã nộp, không thể chỉnh sửa hoặc nộp thêm.`
                }
            />

            <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>
                Thông tin đăng ký dự tuyển
            </Title>

            <Card style={{ marginBottom: 16 }}>
                <Descriptions title="Thông tin cá nhân" bordered>
                    <Descriptions.Item label="Họ và tên" span={3}>
                        <Space><UserOutlined />{data.full_name}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Mã hồ sơ">{data.student_code}</Descriptions.Item>
                    <Descriptions.Item label="CCCD/CMND">{data.identification}</Descriptions.Item>
                    <Descriptions.Item label="Ngày sinh">
                        <Space><CalendarOutlined />{data.date_of_birth ? dayjs(data.date_of_birth).format('DD/MM/YYYY') : ''}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Số điện thoại">
                        <Space><PhoneOutlined />{data.student_phone_number}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Số điện thoại phụ huynh">
                        <Space><PhoneOutlined />{data.parents_phone_number}</Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Địa chỉ liên hệ" span={3}>
                        <Space><EnvironmentOutlined />{data.contact_address}</Space>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <Descriptions title="Thông tin tuyển sinh" bordered>
                    <Descriptions.Item label="Khu vực">{data.admission_area}</Descriptions.Item>
                    <Descriptions.Item label="Đối tượng">{data.admission_group}</Descriptions.Item>
                    <Descriptions.Item label="Năm tốt nghiệp">{data.graduation_year}</Descriptions.Item>
                    <Descriptions.Item label="Ngành xét tuyển ưu tiên">{data.first_admission?.name}</Descriptions.Item>
                    <Descriptions.Item label="Điểm IELTS">{data.ielts_grade || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Giải thưởng HSG cấp tỉnh">
                        {data.provincial_prize ? (
                            <Space>
                                <TrophyOutlined style={{ color: '#faad14' }} />
                                <Tag color={data.provincial_prize === 'first' ? 'gold' : data.provincial_prize === 'second' ? 'blue' : 'orange'}>
                                    {data.provincial_prize}
                                </Tag>
                                {data.provincial_prize_grade && <Tag>Lớp {data.provincial_prize_grade}</Tag>}
                            </Space>
                        ) : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Xếp loại lớp 10">
                        <Tag color={data.grade_10_rank === 'excellent' ? 'green' : 'blue'}>
                            {data.grade_10_rank === 'excellent' ? 'Giỏi' : 'Khác'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Xếp loại lớp 11">
                        <Tag color={data.grade_11_rank === 'excellent' ? 'green' : 'blue'}>
                            {data.grade_11_rank === 'excellent' ? 'Giỏi' : 'Khác'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Xếp loại lớp 12">
                        <Tag color={data.grade_12_rank === 'excellent' ? 'green' : 'blue'}>
                            {data.grade_12_rank === 'excellent' ? 'Giỏi' : 'Khác'}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {renderGrades(data.grades)}
            {renderAdmissions(data.admissions, data.first_admission)}
            {renderAttachments(data.attachments)}
            {renderPayment(data.payment)}
        </>
    );
};

export default StudentProfileReadOnly;
